import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, ChevronDown, Package, Loader2, MessageCircle, Globe, Phone, Headset, Send, ArrowLeftRight } from 'lucide-react';
import SAVisitorPanel from './SAVisitorPanel';
import api from '../../../api/axios';

const intentColors = {
  'Order Inquiry': 'bg-blue-100 text-blue-700',
  'Product Query': 'bg-violet-100 text-violet-700',
  'Delivery': 'bg-cyan-100 text-cyan-700',
  'Refund': 'bg-rose-100 text-rose-700',
  'Payment': 'bg-amber-100 text-amber-700',
  'Discount': 'bg-emerald-100 text-emerald-700',
  'Return': 'bg-orange-100 text-orange-700',
  'General': 'bg-gray-100 text-gray-600',
};

/** Try to extract product URLs from AI message content for product cards. */
function extractProducts(content) {
  if (!content) return [];
  const urlRegex = /https?:\/\/[^\s)]+thesockstreet\.com\/products\/[^\s)]+/gi;
  const matches = content.match(urlRegex);
  if (!matches) return [];
  return matches.map((url) => {
    const slug = url.split('/products/')[1]?.split(/[?#]/)[0] || '';
    const name = slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return { name, url };
  });
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });
}

const SAChatView = ({ chat, onChatUpdate }) => {
  const [showActions, setShowActions] = useState(false);
  const [visitorOpen, setVisitorOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Human takeover state
  const [isHumanTakeover, setIsHumanTakeover] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const replyInputRef = useRef(null);

  // Initialize takeover state from chat prop
  useEffect(() => {
    setIsHumanTakeover(chat?.humanTakeover || false);
  }, [chat?.id]);

  // Fetch messages from all session IDs (merged WhatsApp sessions)
  const fetchAllMessages = async (ids) => {
    const results = await Promise.all(
      ids.map((id) =>
        api.get(`/api/v1/shopping-assistance/sessions/${id}/messages`)
          .then((res) => res.data?.data || [])
          .catch(() => [])
      )
    );
    return results.flat().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  };

  useEffect(() => {
    if (!chat?.id) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const ids = chat.sessionIds?.length ? chat.sessionIds : [chat.id];
    fetchAllMessages(ids)
      .then((all) => { if (!cancelled) setMessages(all); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chat?.id]);

  // Poll for new messages during human takeover
  useEffect(() => {
    if (!chat?.id || !isHumanTakeover) return;
    const ids = chat.sessionIds?.length ? chat.sessionIds : [chat.id];
    const interval = setInterval(async () => {
      try {
        const all = await fetchAllMessages(ids);
        setMessages(all);
      } catch {
        // ignore polling errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [chat?.id, isHumanTakeover]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTakeover = async () => {
    setShowActions(false);
    try {
      await api.post(`/api/v1/shopping-assistance/sessions/${chat.id}/takeover`);
      setIsHumanTakeover(true);
      onChatUpdate?.({ ...chat, humanTakeover: true });
    } catch (err) {
      console.error('Takeover failed:', err);
    }
  };

  const handleHandback = async () => {
    try {
      await api.post(`/api/v1/shopping-assistance/sessions/${chat.id}/handback`);
      setIsHumanTakeover(false);
      setReplyText('');
      onChatUpdate?.({ ...chat, humanTakeover: false });
    } catch (err) {
      console.error('Handback failed:', err);
    }
  };

  const handleSendReply = async () => {
    const text = replyText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/api/v1/shopping-assistance/sessions/${chat.id}/reply`, { message: text });
      const newMsg = res.data?.data;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
      }
      setReplyText('');
    } catch (err) {
      console.error('Send reply failed:', err);
    } finally {
      setSending(false);
      replyInputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  if (!chat) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-gray-400">
        <Bot size={40} className="mb-3 opacity-30" />
        <p className="text-sm">Select a conversation to view</p>
      </div>
    );
  }

  const isActive = chat.status === 'active';
  const isWhatsApp = chat.channel === 'whatsapp';
  const displayName = isWhatsApp && chat.customerName
    ? chat.customerName
    : chat.sessionId?.slice(0, 8) || `#${chat.id}`;
  const avatarInitials = isWhatsApp && chat.customerName
    ? chat.customerName.slice(0, 2).toUpperCase()
    : (chat.sessionId?.slice(0, 2) || `#${chat.id}`.slice(0, 2)).toUpperCase();

  // Filter to human + ai + agent messages for the chat view
  const visibleMessages = messages.filter((m) => m.role === 'human' || m.role === 'ai' || m.role === 'agent');

  return (
    <div className="flex flex-col h-full bg-white relative">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
            isWhatsApp ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
          }`}>
            {avatarInitials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">{displayName}</span>
              {isWhatsApp ? (
                <MessageCircle size={13} className="text-green-500 shrink-0" />
              ) : (
                <Globe size={13} className="text-blue-400 shrink-0" />
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {isActive ? 'active' : chat.status}
              </span>
              {isHumanTakeover && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">
                  Human Control
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {isWhatsApp && chat.customerPhone && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone size={11} /> +{chat.customerPhone}
                </span>
              )}
              {chat.intent && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  intentColors[chat.intent] || 'bg-gray-100 text-gray-600'
                }`}>
                  {chat.intent}
                </span>
              )}
              {chat.agentName && (
                <span className="text-xs text-gray-400">Agent: {chat.agentName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Actions Button */}
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            Actions
            <ChevronDown size={12} />
          </button>
          {showActions && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-10 overflow-hidden">
              <button
                onClick={() => { setVisitorOpen(true); setShowActions(false); }}
                className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
              >
                View Visitor Activity
              </button>
              {isWhatsApp && !isHumanTakeover && (
                <button
                  onClick={handleTakeover}
                  className="w-full text-left px-4 py-2.5 text-xs text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-2"
                >
                  <Headset size={12} />
                  Take Over Chat
                </button>
              )}
              <button
                onClick={() => setShowActions(false)}
                className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Mark as Resolved
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Human Takeover Banner */}
      {isHumanTakeover && (
        <div className="flex items-center justify-between px-5 py-2.5 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-2">
            <Headset size={14} className="text-amber-600" />
            <span className="text-xs font-medium text-amber-700">You are controlling this chat &mdash; AI agent is paused</span>
          </div>
          <button
            onClick={handleHandback}
            className="flex items-center gap-1.5 px-3 py-1 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
          >
            <ArrowLeftRight size={11} />
            Hand Back to AI
          </button>
        </div>
      )}

      {/* Messages */}
      <div className={`flex-1 overflow-y-auto px-5 py-4 space-y-4 ${isWhatsApp && isHumanTakeover ? 'pb-20' : ''}`}>
        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {!loading && visibleMessages.map((msg) => {
          const isUser = msg.role === 'human';
          const isAgent = msg.role === 'agent';
          const products = (!isUser && !isAgent) ? extractProducts(msg.content) : [];

          return (
            <div key={msg.id} className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                isUser ? 'bg-gray-100' : isAgent ? 'bg-indigo-100' : 'bg-blue-100'
              }`}>
                {isUser ? (
                  <User size={14} className="text-gray-500" />
                ) : isAgent ? (
                  <Headset size={14} className="text-indigo-600" />
                ) : (
                  <Bot size={14} className="text-blue-600" />
                )}
              </div>
              <div className={`flex flex-col gap-1 max-w-[70%] ${isUser ? 'items-end' : 'items-start'}`}>
                {isAgent && (
                  <span className="text-xs font-medium text-indigo-500">You</span>
                )}
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  isUser
                    ? 'bg-blue-500 text-white rounded-tr-sm'
                    : isAgent
                    ? 'bg-indigo-500 text-white rounded-tl-sm'
                    : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                }`}>
                  {msg.content}
                </div>
                {products.map((p, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 mt-1 shadow-sm">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Package size={18} className="text-gray-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-800">{p.name}</p>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                        View Product
                      </a>
                    </div>
                  </div>
                ))}
                <span className="text-xs text-gray-400">{formatTime(msg.createdAt)}</span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Human Reply Input (sticky at bottom of chat panel) */}
      {isWhatsApp && isHumanTakeover && (
        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t border-gray-200 bg-gray-50 z-10">
          <div className="flex items-center gap-2">
            <input
              ref={replyInputRef}
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your reply to the customer..."
              disabled={sending}
              className="flex-1 px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 disabled:opacity-50 placeholder-gray-400"
            />
            <button
              onClick={handleSendReply}
              disabled={!replyText.trim() || sending}
              className="flex items-center justify-center w-10 h-10 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Tool Activity Panel */}
      {visitorOpen && (
        <SAVisitorPanel sessionId={chat.id} onClose={() => setVisitorOpen(false)} />
      )}
    </div>
  );
};

export default SAChatView;
